import { Assets, Container, Graphics, Sprite, Text, TextStyle, type Texture } from 'pixi.js';
import { FlipbookCharacter, buildFramePaths } from '../characters/FlipbookCharacter.js';
import { detectLocale, type Locale, type Speaker } from '../story/caseModel.js';
import { Countdown } from './Countdown.js';
import { SpeechBubble } from './SpeechBubble.js';
import type { CastMember, Gesture } from '../story/castDialogue.js';

/**
 * The staged Case 000 set: the approved lab artwork, Patch and Bit standing in
 * it, the countdown and the evidence readout. This is what turns the case from
 * a list of questions into a place.
 *
 * All artwork is the approved pilot artwork, re-encoded only
 * (docs/animation/VISUAL_ANIMATION_GUIDE.md) — nothing is repainted.
 */
const CHARACTER_ANIMATIONS = [
  { name: 'idle', frames: 24, fps: 12, loop: true },
  { name: 'alert', frames: 16, fps: 12, loop: true },
  { name: 'react-positive', frames: 18, fps: 12, loop: false },
  { name: 'react-negative', frames: 18, fps: 12, loop: false },
  { name: 'solved', frames: 16, fps: 12, loop: true },
  { name: 'partial', frames: 16, fps: 12, loop: true },
  { name: 'incorrect', frames: 16, fps: 12, loop: true },
] as const;

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
/** "The node will be disconnected in ten minutes" — the case's opening line. */
const COUNTDOWN_SECONDS = 600;
const SPEAKER_COLOR: Record<'patch' | 'bit', number> = { patch: 0x9fe8c0, bit: 0xffb366 };
const HUD_COPY: Record<Locale, { readonly countdown: string; readonly evidence: string }> = {
  'en-US': { countdown: 'MESSAGE’S COUNTDOWN', evidence: 'EVIDENCE' },
  'fr-FR': { countdown: 'DÉCOMPTE DU MESSAGE', evidence: 'PREUVES' },
  'zh-CN': { countdown: '消息中的倒计时', evidence: '证据' },
};

async function loadCharacter(baseDir: string): Promise<FlipbookCharacter> {
  return FlipbookCharacter.load(
    CHARACTER_ANIMATIONS.map((animation) => ({
      name: animation.name,
      frameUrls: buildFramePaths(`${baseDir}/${animation.name}`, animation.frames, 3, 'webp'),
      fps: animation.fps,
      loop: animation.loop,
    })),
  );
}

interface CharacterSlot {
  readonly name: 'patch' | 'bit';
  readonly character: FlipbookCharacter;
  readonly baseScale: number;
  readonly baseX: number;
  readonly baseY: number;
  readonly parallax: number;
  /** Offsets each character's sway/talk-bob cycle so they don't move in lockstep. */
  readonly phaseOffset: number;
  popMs: number;
  /** Smoothed toward a target each frame by the speak()/clearSpeech() focus effect. */
  focusAlpha: number;
  focusScale: number;
}

export class SceneStage {
  readonly view = new Container();
  private background!: Sprite;
  private backgroundBaseX = 0;
  private backgroundBaseY = 0;
  private slots: CharacterSlot[] = [];
  /** The scene's ambient loop (idle/alert/an ending), restored once nobody's "speaking". */
  private currentMood = 'idle';
  private speakerFocus: Speaker = null;
  private gesture: Gesture = 'think';
  private gestureMs = 0;
  private onConsult: ((speaker: CastMember) => void) | undefined;
  private readonly badges: Text[] = [];
  private consultEnabled = false;
  private bubble!: SpeechBubble;
  private countdown!: Countdown;
  private evidenceText!: Text;
  private readonly flash = new Graphics();
  private flashMs = 0;
  private flashColor = 0x9fe8c0;
  private elapsedMs = 0;
  private width = 0;
  private height = 0;
  private readonly hudCopy = HUD_COPY[detectLocale()];

  static async create(width: number, height: number): Promise<SceneStage> {
    const stage = new SceneStage();
    await stage.build(width, height);
    return stage;
  }

  private async build(width: number, height: number): Promise<void> {
    this.width = width;
    this.height = height;

    const [backgroundTexture, patch, bit] = await Promise.all([
      Assets.load<Texture>('/scene/lab-background.webp'),
      loadCharacter('/characters/patch'),
      loadCharacter('/characters/bit'),
    ]);

    // Cover-fit the lab so the alert screens stay visible and the wet floor
    // lands at the bottom of the band, where the characters stand.
    this.background = new Sprite(backgroundTexture);
    this.background.anchor.set(0.5, 1);
    const cover =
      Math.max(width / backgroundTexture.width, height / backgroundTexture.height) * 1.08;
    this.background.scale.set(cover);
    this.backgroundBaseX = width / 2;
    this.backgroundBaseY = height;
    this.background.position.set(this.backgroundBaseX, this.backgroundBaseY);
    this.view.addChild(this.background);

    // Darken the lab slightly so the UI and characters read against it.
    this.view.addChild(
      new Graphics().rect(0, 0, width, height).fill({ color: 0x050a0b, alpha: 0.28 }),
    );

    // The blend into the console is drawn BEFORE the cast, so it darkens the
    // set without washing the characters out.
    this.buildGradient();

    const floorY = height * 0.97;
    this.slots = [
      this.placeCharacter(patch, 'patch', width * 0.28, floorY, Math.min(width * 0.68, 280), 1),
      this.placeCharacter(bit, 'bit', width * 0.76, floorY, Math.min(width * 0.48, 190), -0.6),
    ];

    this.buildHud();
    for (const slot of this.slots) {
      const badge = new Text({
        text: `${slot.name.toUpperCase()} · +`,
        style: {
          fontFamily: MONO,
          fontSize: 11,
          fontWeight: '700',
          fill: SPEAKER_COLOR[slot.name],
        },
      });
      badge.anchor.set(0.5, 1);
      badge.position.set(slot.baseX, height - 7);
      this.view.addChild(badge);
      this.badges.push(badge);
      for (const target of [badge, slot.character.sprite]) {
        target.eventMode = 'static';
        target.cursor = 'pointer';
        target.on('pointertap', () => {
          if (this.consultEnabled) this.onConsult?.(slot.name);
        });
      }
    }

    // Added last so it draws on top of the cast and the HUD.
    this.bubble = new SpeechBubble(width, Math.min(width * 0.74, 260));
    this.view.addChild(this.bubble.view);

    this.flash.rect(0, 0, width, height).fill({ color: 0xffffff });
    this.flash.alpha = 0;
    this.flash.eventMode = 'none';
    this.view.addChild(this.flash);
  }

  private placeCharacter(
    character: FlipbookCharacter,
    name: 'patch' | 'bit',
    x: number,
    feetY: number,
    targetWidth: number,
    parallax: number,
  ): CharacterSlot {
    const sprite = character.sprite;
    const baseScale = targetWidth / sprite.texture.width;
    sprite.scale.set(baseScale);
    sprite.position.set(x, feetY);
    this.view.addChild(sprite);
    return {
      name,
      character,
      baseScale,
      baseX: x,
      baseY: feetY,
      parallax,
      phaseOffset: name === 'patch' ? 0 : Math.PI * 0.6,
      popMs: 0,
      focusAlpha: 1,
      focusScale: 1,
    };
  }

  /** Fake vertical gradient blending the set into the console panel below. */
  private buildGradient(): void {
    const gradient = new Graphics();
    const bands = 14;
    const bandHeight = this.height * 0.3;
    for (let i = 0; i < bands; i++) {
      const alpha = (i / (bands - 1)) ** 2 * 0.95;
      gradient
        .rect(
          0,
          this.height - bandHeight + (i * bandHeight) / bands,
          this.width,
          bandHeight / bands + 1,
        )
        .fill({ color: 0x04080a, alpha });
    }
    this.view.addChild(gradient);
  }

  private buildHud(): void {
    this.countdown = new Countdown(COUNTDOWN_SECONDS, 1);
    this.countdown.view.position.set(this.width - 100, 26);
    this.view.addChild(this.countdown.view);

    const label = new Text({
      text: this.hudCopy.countdown,
      style: new TextStyle({ fill: 0xff6a5a, fontFamily: MONO, fontSize: 8, letterSpacing: 1 }),
    });
    label.anchor.set(1, 1);
    label.position.set(this.width - 12, 12);
    this.view.addChild(label);

    this.evidenceText = new Text({
      text: '',
      style: new TextStyle({ fill: 0x7fe0a8, fontFamily: MONO, fontSize: 11 }),
    });
    this.evidenceText.position.set(12, 14);
    this.view.addChild(this.evidenceText);
  }

  setEvidence(count: number, total: number): void {
    this.evidenceText.text = `${this.hudCopy.evidence} ${String(count).padStart(2, '0')}/${String(total)}`;
  }

  /** Sets both characters' resting mood (idle / alert / an ending loop), and
   * clears any speech — the base state speak() layers onto. */
  setMood(animation: string): void {
    this.currentMood = animation;
    this.speakerFocus = null;
    this.bubble.hide();
    for (const slot of this.slots) slot.character.play(animation);
  }

  /**
   * Patch or Bit poses `text` in a speech bubble anchored over their head:
   * their gesture controls the motion and mood, while the listener recedes
   * slightly. Resolves on player acknowledgement, or on cancellation.
   */
  async speak(speaker: CastMember, text: string, gesture: Gesture = 'think'): Promise<void> {
    this.speakerFocus = speaker;
    this.gesture = gesture;
    this.gestureMs = 0;
    for (const slot of this.slots) {
      const speakingMood = gesture === 'urgent' || gesture === 'concern' ? 'alert' : 'idle';
      slot.character.play(speaker === slot.name ? speakingMood : this.currentMood);
    }
    const slot = this.slots.find((candidate) => candidate.name === speaker);
    if (slot === undefined) return;
    const sprite = slot.character.sprite;
    const anchorX = slot.baseX;
    const anchorY = slot.baseY - sprite.texture.height * slot.baseScale * 0.88;
    await this.bubble.type(text, anchorX, anchorY, SPEAKER_COLOR[speaker], speaker.toUpperCase());
  }

  /** Dismisses the bubble and returns both characters to the ambient mood. */
  clearSpeech(): void {
    this.speakerFocus = null;
    this.bubble.hide();
    for (const slot of this.slots) slot.character.play(this.currentMood);
  }

  /** Plays a one-shot reaction that settles back into `mood`, with a foreground pop. */
  playReaction(reaction: string, mood: string): void {
    this.currentMood = mood;
    this.speakerFocus = null; // the player's own action takes over from whoever was "asking"
    this.bubble.hide();
    for (const slot of this.slots) {
      // Patch is expressive; Bit stays grounded and follows the evidence.
      slot.character.play(slot.name === 'patch' ? reaction : mood, mood);
      slot.popMs = slot.name === 'patch' ? 520 : 0;
    }
    this.flashColor = reaction === 'react-negative' ? 0xff4b3a : 0x7fffb0;
    this.flashMs = 420;
  }

  get countdownExpired(): boolean {
    return this.countdown.expired;
  }

  /** Restarts the ten-minute countdown, so a replay opens under fresh pressure. */
  resetCountdown(): void {
    this.countdown.reset(COUNTDOWN_SECONDS);
  }

  /** The timer stays frozen during exposition and starts with player agency. */
  startCountdown(): void {
    this.countdown.start();
  }

  pauseCountdown(): void {
    this.countdown.pause();
  }

  setOnConsult(callback: (speaker: CastMember) => void): void {
    this.onConsult = callback;
  }

  setConsultEnabled(enabled: boolean): void {
    this.consultEnabled = enabled;
    const locale = detectLocale();
    const action = locale === 'fr-FR' ? 'parler' : locale === 'zh-CN' ? '交谈' : 'talk';
    this.badges.forEach((badge, index) => {
      badge.text = `${this.slots[index]?.name.toUpperCase() ?? ''}${enabled ? ` · ${action}` : ''}`;
      badge.alpha = enabled ? 1 : 0.6;
    });
  }

  update(deltaMs: number): void {
    this.elapsedMs += deltaMs;
    this.gestureMs += deltaMs;
    this.countdown.update(deltaMs);
    this.bubble.update(deltaMs);

    // Slow breathing parallax so the set never feels like a static picture.
    const drift = Math.sin(this.elapsedMs / 5200) * 7;
    const rise = Math.sin(this.elapsedMs / 7100) * 4;
    this.background.position.set(this.backgroundBaseX + drift, this.backgroundBaseY + rise);

    for (const slot of this.slots) {
      const sprite = slot.character.sprite;
      sprite.x = slot.baseX - drift * 0.35 * slot.parallax;

      // Slow ambient sway; restrained speaker gestures only while text reveals.
      // Patch is quicker and more expressive than Bit, who stays grounded.
      const ambientPhase = this.elapsedMs / 900 + slot.phaseOffset;
      let bobY = Math.sin(ambientPhase) * 2.2;
      let tilt = Math.sin(ambientPhase * 0.8) * 0.02;

      const isActiveSpeaker = this.speakerFocus === slot.name;
      if (isActiveSpeaker && this.bubble.typing) {
        const talkPhase = this.gestureMs / (slot.name === 'patch' ? 290 : 440);
        const energy = this.gesture === 'urgent' ? 2.5 : this.gesture === 'relief' ? 0.8 : 1.4;
        bobY += Math.sin(talkPhase) * energy;
        tilt += Math.sin(talkPhase * 0.7) * energy * 0.007;
        if (this.gesture === 'think') tilt += slot.name === 'bit' ? -0.018 : 0.018;
      }
      sprite.y = slot.baseY + bobY;
      sprite.rotation = tilt;

      // Lean toward whoever's "asking" and dim the other one; neutral (both at
      // rest) once speakerFocus is null.
      const isSpeaker = this.speakerFocus === null || isActiveSpeaker;
      const targetAlpha = this.speakerFocus === null ? 1 : isSpeaker ? 1 : 0.85;
      const targetFocusScale = this.speakerFocus === null ? 1 : isSpeaker ? 1.035 : 0.985;
      const ease = Math.min(1, deltaMs / 180);
      slot.focusAlpha += (targetAlpha - slot.focusAlpha) * ease;
      slot.focusScale += (targetFocusScale - slot.focusScale) * ease;
      sprite.alpha = slot.focusAlpha;

      let pop = 0;
      if (slot.popMs > 0) {
        slot.popMs = Math.max(0, slot.popMs - deltaMs);
        const phase = 1 - slot.popMs / 520;
        pop = Math.sin(Math.PI * phase) * 0.09;
      }
      sprite.scale.set(slot.baseScale * (1 + pop) * slot.focusScale);
    }

    if (this.flashMs > 0) {
      this.flashMs = Math.max(0, this.flashMs - deltaMs);
      const phase = this.flashMs / 420;
      this.flash.tint = this.flashColor;
      this.flash.alpha = phase * phase * 0.15;
    }
  }

  destroy(): void {
    this.bubble.hide();
    this.view.destroy({ children: true });
  }
}
